import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Modal } from 'antd';
import { FaEdit, FaBuilding, FaMapMarkerAlt, FaPhone, FaEnvelope, FaGlobe, FaCertificate, FaLanguage, FaShoppingBag, FaDollarSign, FaCalendarAlt, FaUsers, FaCheckCircle, FaTimesCircle, FaClock, FaIndustry, FaList } from 'react-icons/fa';
import CommonService from '../../services/common.service';
import { toast } from 'react-toastify';
import '../../assets/styles/view-profile.scss';
import { getProfileRoute } from '../../utils/getProfileRoute';
import { getProfileRouteByRole, getCurrentRole } from '../../utils/roleSwitcher';
import { useBusinessTypeMaster } from '../../hooks/useBusinessTypeMaster';
import { useFactoryDetailsMaster } from '../../hooks/useFactoryDetailsMaster';
import { useLocationMaster } from '../../hooks/useLocationMaster';
import { useCurrencyMaster } from '../../hooks/useCurrencyMaster';
import { API_BASE_URL, API_URLS } from '../../constants/api-urls';
import BuyerRfq from '../buyer seller/Buyer/buyer_rfq';

export default function ViewProfile() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [factoryDetails, setFactoryDetails] = useState(null);
  const [qualityDetails, setQualityDetails] = useState(null);
  const { businessTypeOptions, loading: businessTypesLoading } = useBusinessTypeMaster();
  const [businessTypeLabel, setBusinessTypeLabel] = useState(null);
  const [activeTab, setActiveTab] = useState('profile');
  const [contractManufacturerModalVisible, setContractManufacturerModalVisible] = useState(false);
  const [contractManufacturerStatusSaving, setContractManufacturerStatusSaving] = useState(false);
  
  // Factory Details Master for shipping terms, mode of transport, payment methods, and payment policies
  const { 
    shippingTermsOptions, 
    modeOfTransportOptions,
    paymentMethodOptions,
    paymentScheduleOptions,
    latePaymentPolicyOptions
  } = useFactoryDetailsMaster();
  
  // Location Master for dispatch location (countries, states, cities)
  const { 
    countries: rawCountries, 
    states: rawStates, 
    cities: rawCities,
    updateListsForCountry,
    updateListsForState
  } = useLocationMaster();
  const countries = rawCountries.map((c) => ({ value: c.id, label: c.label }));
  const states = rawStates.map((s) => ({ value: s.id, label: s.label }));
  const cities = rawCities.map((c) => ({ value: c.id, label: c.label }));
  
  // Currency Master for currency
  const { currencyOptions } = useCurrencyMaster();
  
  // Helper function to find label from ID
  const findLabelById = (options, id) => {
    if (!id || !options || options.length === 0) return id || 'N/A';
    const found = options.find(opt => 
      String(opt.value) === String(id) || 
      Number(opt.value) === Number(id) ||
      opt.value === id
    );
    return found ? found.label : id;
  };

  useEffect(() => {
    loadProfile();
  }, []); // Reload profile when component mounts

  // Reload profile when role changes (listen to storage events)
  useEffect(() => {
    const handleStorageChange = () => {
      loadProfile();
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Update business type label when businessTypeOptions are loaded
  useEffect(() => {
    if (profile?.company_type_id && businessTypeOptions.length > 0 && !businessTypesLoading) {
      const businessType = businessTypeOptions.find(bt => 
        String(bt.value) === String(profile.company_type_id) ||
        Number(bt.value) === Number(profile.company_type_id) ||
        bt.value === profile.company_type_id
      );
      setBusinessTypeLabel(businessType?.label || null);
    } else if (profile?.company_type_id && businessTypeOptions.length === 0 && !businessTypesLoading) {
      // If options are loaded but no match found, clear the label to show ID
      setBusinessTypeLabel(null);
    }
  }, [profile?.company_type_id, businessTypeOptions, businessTypesLoading]);

  const loadProfile = async () => {
    // Validate user_type_id to ensure we're loading the correct profile type
    // This prevents buyers/sellers from accidentally trying to load Quality Inspector profile
    const user = JSON.parse(localStorage.getItem('trocanet_user') || sessionStorage.getItem('trocanet_user') || '{}');
    let userTypeId = user.user_type_id;
    if (userTypeId === null || userTypeId === undefined) {
      userTypeId = '';
    } else {
      userTypeId = String(userTypeId).trim();
    }
    const typeIds = userTypeId 
      ? userTypeId.split(',').map(id => String(id).trim()).filter(id => id && id !== 'null' && id !== 'undefined')
      : [];
    
    // Check if user actually has Quality Inspector user_type_id (4)
    const hasQualityInspectorId = typeIds.includes('4') || userTypeId === '4';
    
    try {
      setLoading(true);
      setError(null);
      
      // Get current role to determine which endpoint to use
      const currentRole = getCurrentRole();
      
      let response;
      
      // Load profile based on current role AND user_type_id validation
      if (currentRole && currentRole.id === 'quality_inspector' && hasQualityInspectorId) {
        // Load Quality Inspector profile - only if user_type_id = 4
        response = await CommonService.get('/quality-inspector/my-profile');
      } else {
        // Load Buyer/Seller company profile
        response = await CommonService.get('/company/my-profile');
      }
      
      if (response && response.success) {
        if (response.data) {
          setProfile(response.data);
          
          // Load location data for Quality Inspector profile
          if (currentRole && currentRole.id === 'quality_inspector' && hasQualityInspectorId && response.data.country_id) {
            await updateListsForCountry(response.data.country_id);
            if (response.data.state_id) {
              await updateListsForState(response.data.country_id, response.data.state_id);
            }
          }
          
          // Load factory details and quality control only for company profiles (Buyer/Seller)
          if (!hasQualityInspectorId) {
            const profileData = response.data;
            if (profileData.company_id && (profileData.company_role === 'SELLER' || profileData.company_role === 'BOTH')) {
              try {
                const factoryResponse = await CommonService.get(`/profile/company/${profileData.company_id}/factories`);
                if (factoryResponse && factoryResponse.success && factoryResponse.data) {
                  const factories = Array.isArray(factoryResponse.data) ? factoryResponse.data : [factoryResponse.data];
                  if (factories.length > 0 && factories[0]) {
                    // Factory data loaded
                  }
                  setFactoryDetails(factories.length > 0 ? factories : null);
                }
              } catch (factoryErr) {
                console.log('No factory details found or error loading factory:', factoryErr);
                // Don't show error as factory details are optional
              }
              
              // Load quality control details
              try {
                const qualityResponse = await CommonService.get(`/profile/company/${profileData.company_id}/quality`);
                if (qualityResponse && qualityResponse.success && qualityResponse.data) {
                  const qualityDataArray = Array.isArray(qualityResponse.data) ? qualityResponse.data : [qualityResponse.data];
                  setQualityDetails(qualityDataArray.length > 0 ? qualityDataArray[0] : null);
                }
              } catch (qualityErr) {
                console.log('No quality control details found or error loading quality:', qualityErr);
                // Don't show error as quality control details are optional
              }
            }
          }
        } else {
          // Profile doesn't exist yet - show appropriate message based on user_type_id
          let roleName = 'Company';
          if (hasQualityInspectorId) {
            roleName = 'Quality Inspector';
          } else if (currentRole) {
            roleName = currentRole.name;
          }
          setError(`${roleName} profile not found. Please create your ${roleName.toLowerCase()} profile first.`);
        }
      } else {
        setError('Failed to load profile. Please try again.');
      }
    } catch (err) {
      console.error('Error loading profile:', err);
      let message = err?.response?.data?.message || 'Failed to load profile. Please try again.';
      
      // If error mentions Quality Inspector but user is not a Quality Inspector, show generic message
      const hasQualityInspectorId = typeIds.includes('4') || userTypeId === '4';
      if (!hasQualityInspectorId && message.toLowerCase().includes('quality inspector')) {
        message = 'Company profile not found. Please create your company profile first.';
      }
      
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const getEditRoute = () => {
    // Use role switcher to get the profile route based on current selected role
    try {
      const roleBasedRoute = getProfileRouteByRole();
      if (roleBasedRoute) {
        return roleBasedRoute;
      }
    } catch (e) {
      // Fallback to original logic if role switcher fails
    }
    
    // Fallback to original logic
    const user = JSON.parse(localStorage.getItem('trocanet_user') || '{}');
    const userTypeId = String(user.user_type_id || '').trim();
    const typeIds = userTypeId ? userTypeId.split(',').map(id => String(id).trim()) : [];
    
    if (typeIds.includes('2') || userTypeId === '2') {
      return '/seller-profile';
    }
    if (typeIds.includes('1') || userTypeId === '1') {
      return '/buyer-profile';
    }
    return '/buyer-profile';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    } catch {
      return dateString;
    }
  };

  const formatYear = (year) => {
    if (!year) return 'N/A';
    return year;
  };

  // Check if user is a buyer
  const isBuyer = () => {
    // Check user_type_id from localStorage
    const user = JSON.parse(localStorage.getItem('trocanet_user') || '{}');
    const userTypeId = String(user.user_type_id || '').trim();
    const typeIds = userTypeId ? userTypeId.split(',').map(id => String(id).trim()) : [];
    
    // Check if user_type_id includes '1' (Buyer)
    const isBuyerByUserType = typeIds.includes('1') || userTypeId === '1';
    
    // Also check company_role from profile
    const isBuyerByRole = profile && (profile.company_role === 'BUYER' || profile.company_role === 'BOTH');
    
    return isBuyerByUserType || isBuyerByRole;
  };

  // Check if current profile is Quality Inspector
  const isQualityInspector = () => {
    const currentRole = getCurrentRole();
    return currentRole && currentRole.id === 'quality_inspector';
  };

  // Check if logged-in user's role is Seller (show Contract Manufacturer prompt only for Seller)
  const isSeller = profile && (profile.company_role === 'SELLER' || profile.company_role === 'BOTH');

  // Contract manufacturer: opted_out = hide button; opted_in = show "View Contract Manufacturer"; null = show "Are you a Contract Manufacturer?"
  const cmStatus = profile?.contract_manufacturer_status;
  const showContractManufacturerButton = isSeller && cmStatus !== 'opted_out';
  const isContractManufacturerOptedIn = cmStatus === 'opted_in';

  const handleContractManufacturerChoice = async (choice) => {
    const status = choice ? 'opted_in' : 'opted_out';
    setContractManufacturerStatusSaving(true);
    try {
      await CommonService.put(API_URLS.CONTRACT_MANUFACTURER.UPDATE_STATUS, { status });
      setProfile((prev) => (prev ? { ...prev, contract_manufacturer_status: status } : prev));
      setContractManufacturerModalVisible(false);
      toast.success('Your selection has been saved.');
      if (choice) {
        navigate('/contract-manufacturer-profile');
      }
    } catch (err) {
      console.error('Error saving contract manufacturer status:', err);
      toast.error(err?.response?.data?.message || 'Failed to save. Please try again.');
    } finally {
      setContractManufacturerStatusSaving(false);
    }
  };

  // Helper to get full URL for file paths
  const getFileUrl = (filePath) => {
    if (!filePath) return '';
    // Ensure filePath is a string
    if (typeof filePath !== 'string') {
      filePath = String(filePath);
    }
    if (filePath.startsWith('http')) return filePath;
    return `${API_BASE_URL.replace('/api/v1', '')}${filePath.startsWith('/') ? '' : '/'}${filePath}`;
  };

  // Helper to get file name from path
  const getFileNameFromPath = (filePath) => {
    if (!filePath) return 'View File';
    // Ensure filePath is a string
    if (typeof filePath !== 'string') {
      filePath = String(filePath);
    }
    const parts = filePath.split('/');
    return parts[parts.length - 1] || 'View File';
  };


  if (loading) {
    return (
      <div className="view-profile-container">
        <div className="view-profile-loading">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (error && !profile) {
    return (
      <div className="view-profile-container">
        <div className="view-profile-error">
          <FaTimesCircle className="error-icon" />
          <h3>Profile Not Found</h3>
          <p>{error}</p>
          <button 
            className="btn btn-primary mt-3" 
            onClick={() => navigate(getEditRoute())}
          >
            Create Profile
          </button>
        </div>
      </div>
    );
  }

  if (!profile) {
    return null;
  }

  return (
    <>
      <div className="view-profile-container">
        <div className="container-fluid py-4">
          {/* Header */}
          <div className="view-profile-header mb-4">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h2 className="view-profile-title">
                  <FaBuilding className="me-2" />
                  My Profile
                </h2>
                <p className="text-muted mb-0">View your complete company profile</p>
              </div>
              {activeTab === 'profile' && (
                <div className="d-flex align-items-center gap-2">
                  {showContractManufacturerButton && (
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      onClick={() => {
                        if (isContractManufacturerOptedIn) {
                          navigate('/contract-manufacturer-profile');
                        } else {
                          setContractManufacturerModalVisible(true);
                        }
                      }}
                      disabled={contractManufacturerStatusSaving}
                    >
                      {isContractManufacturerOptedIn ? 'View Contract Manufacturer' : 'Are you a Contract Manufacturer?'}
                    </button>
                  )}
                  <Modal
                    title="Contract Manufacturer"
                    open={contractManufacturerModalVisible}
                    onCancel={() => !contractManufacturerStatusSaving && setContractManufacturerModalVisible(false)}
                    footer={[
                      <button key="no" type="button" className="btn btn-outline-primary" onClick={() => handleContractManufacturerChoice(false)} disabled={contractManufacturerStatusSaving}>No</button>,
                      <button key="yes" type="button" className="btn btn-primary" onClick={() => handleContractManufacturerChoice(true)} disabled={contractManufacturerStatusSaving}>{contractManufacturerStatusSaving ? 'Saving...' : 'Yes'}</button>,
                    ]}
                  >
                    <p className="mb-0">Do you want to become a Contract Manufacturer?</p>
                  </Modal>
                  <Link 
                    to={getEditRoute()} 
                    className="btn btn-primary"
                  >
                    <FaEdit className="me-2" />
                    Edit Profile
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Tabs */}
          <ul className="nav nav-tabs mb-4" role="tablist">
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === 'profile' ? 'active' : ''}`}
                onClick={() => setActiveTab('profile')}
                type="button"
                role="tab"
              >
                <FaBuilding className="me-2" />
                My Profile
              </button>
            </li>
            {isBuyer() && (
              <li className="nav-item" role="presentation">
                <button
                  className={`nav-link ${activeTab === 'rfq' ? 'active' : ''}`}
                  onClick={() => setActiveTab('rfq')}
                  type="button"
                  role="tab"
                >
                  <FaList className="me-2" />
                  My RFQ List
                </button>
              </li>
            )}
          </ul>

          {/* Tab Content */}
          {activeTab === 'profile' && (
            <div>

          {/* Profile Status */}
          {profile.verification_status && (
            <div className={`alert alert-${profile.verification_status === 'VERIFIED' ? 'success' : profile.verification_status === 'REJECTED' ? 'danger' : 'warning'} mb-4`}>
              <div className="d-flex align-items-center flex-wrap">
                {profile.verification_status === 'VERIFIED' ? (
                  <>
                    <FaCheckCircle className="me-2" />
                    <strong>Verification Status:</strong>
                    <span className="ms-2 text-capitalize">{profile.verification_status}</span>
                  </>
                ) : profile.verification_status === 'REJECTED' ? (
                  <>
                    <FaTimesCircle className="me-2" />
                    <strong>Verification Status:</strong>
                    <span className="ms-2 text-capitalize">{profile.verification_status}</span>
                    <span className="ms-2 text-danger">Your profile has been rejected. Please contact support for more information.</span>
                  </>
                ) : (
                  <>
                    <FaClock className="me-2" />
                    <strong>Verification Status:</strong>
                    <span className="ms-2 text-capitalize">{profile.verification_status}</span>
                    <span className="ms-2 text-muted">Your profile is pending admin review. This is normal and your profile will be verified soon.</span>
                  </>
                )}
                {profile.profile_completion !== undefined && (
                  <span className="ms-auto">
                    Profile Completion: <strong>{profile.profile_completion}%</strong>
                    {profile.profile_completion < 100 && (
                      <small className="ms-2 text-muted d-block">Complete all fields to reach 100%</small>
                    )}
                  </span>
                )}
              </div>
            </div>
          )}

          <div className="row g-4">
            {/* Left Column - Main Info */}
            <div className="col-lg-8">
              {/* Quality Inspector Profile */}
              {isQualityInspector() ? (
                <>
                  {/* Personal Information */}
                  <div className="view-profile-card mb-4">
                    <h5 className="view-profile-card-title">
                      <FaBuilding className="me-2" />
                      Personal Information
                    </h5>
                    <div className="row g-3">
                      {/* Profile Picture */}
                      {profile.profile_picture && (
                        <div className="col-12 text-center mb-3">
                          <img 
                            src={getFileUrl(profile.profile_picture)}
                            alt="Profile Picture"
                            style={{
                              maxWidth: '200px',
                              maxHeight: '200px',
                              objectFit: 'cover',
                              borderRadius: '8px',
                              border: '1px solid #ddd'
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        </div>
                      )}
                      <div className="col-md-6">
                        <div className="view-profile-field">
                          <label>Contact Name</label>
                          <p>{profile.name || 'N/A'}</p>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="view-profile-field">
                          <label>Name of Organization</label>
                          <p>{profile.company_name || 'N/A'}</p>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="view-profile-field">
                          <label>Languages Known</label>
                          <p>{profile.language_id_str ? profile.language_id_str.split(',').map(id => {
                            const langMap = { '1': 'English', '2': 'Hindi', '3': 'Spanish', '4': 'French', '5': 'German', '6': 'Chinese', '7': 'Japanese', '8': 'Arabic' };
                            return langMap[id.trim()] || id;
                          }).join(', ') : 'N/A'}</p>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="view-profile-field">
                          <label>GST Number</label>
                          <p>{profile.gst_no || 'N/A'}</p>
                        </div>
                      </div>
                      {profile.bio && (
                        <div className="col-12">
                          <div className="view-profile-field">
                            <label>Professional Summary</label>
                            <p>{profile.bio}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Contact Information */}
                  <div className="view-profile-card mb-4">
                    <h5 className="view-profile-card-title">
                      <FaPhone className="me-2" />
                      Contact Information
                    </h5>
                    <div className="row g-3">
                      <div className="col-md-6">
                        <div className="view-profile-field">
                          <label>Email</label>
                          <p>
                            <a href={`mailto:${profile.email}`}>
                              <FaEnvelope className="me-2" />
                              {profile.email || 'N/A'}
                            </a>
                          </p>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="view-profile-field">
                          <label>Phone Number</label>
                          <p>
                            <a href={`tel:${profile.phone}`}>
                              <FaPhone className="me-2" />
                              {profile.phone || 'N/A'}
                            </a>
                          </p>
                        </div>
                      </div>
                      {profile.alternate_contact && (
                        <div className="col-md-6">
                          <div className="view-profile-field">
                            <label>Alternate Contact</label>
                            <p>{profile.alternate_contact}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Address Information */}
                  {(profile.address || profile.country_id || profile.state_id || profile.city_id || profile.pincode) && (
                    <div className="view-profile-card mb-4">
                      <h5 className="view-profile-card-title">
                        <FaMapMarkerAlt className="me-2" />
                        Address Information
                      </h5>
                      <div className="row g-3">
                        {profile.address && (
                          <div className="col-12">
                            <div className="view-profile-field">
                              <label>Address</label>
                              <p>{profile.address}</p>
                            </div>
                          </div>
                        )}
                        <div className="col-md-4">
                          <div className="view-profile-field">
                            <label>Country</label>
                            <p>{findLabelById(countries, profile.country_id) || 'N/A'}</p>
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="view-profile-field">
                            <label>State</label>
                            <p>{findLabelById(states, profile.state_id) || 'N/A'}</p>
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="view-profile-field">
                            <label>City</label>
                            <p>{findLabelById(cities, profile.city_id) || 'N/A'}</p>
                          </div>
                        </div>
                        {profile.pincode && (
                          <div className="col-md-6">
                            <div className="view-profile-field">
                              <label>Pincode</label>
                              <p>{profile.pincode}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Resume */}
                  {profile.resume && (
                    <div className="view-profile-card mb-4">
                      <h5 className="view-profile-card-title">
                        <FaCertificate className="me-2" />
                        Resume
                      </h5>
                      <div className="view-profile-field">
                        <a 
                          href={getFileUrl(profile.resume)} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="btn btn-primary"
                        >
                          View Resume
                        </a>
                      </div>
                    </div>
                  )}

                  {/* Profile Data - Specializations, Services, Education, Experience, Certifications, etc. */}
                  {profile.profile_data && (() => {
                    const profileData = typeof profile.profile_data === 'string' 
                      ? JSON.parse(profile.profile_data) 
                      : profile.profile_data;
                    
                    return (
                      <>
                        {/* Specializations */}
                        {(profileData.specializations?.primary?.length > 0 || profileData.specializations?.sub?.length > 0) && (
                          <div className="view-profile-card mb-4">
                            <h5 className="view-profile-card-title">
                              <FaIndustry className="me-2" />
                              Specializations
                            </h5>
                            <div className="row g-3">
                              {profileData.specializations?.primary?.length > 0 && (
                                <div className="col-md-6">
                                  <div className="view-profile-field">
                                    <label>Primary Specializations</label>
                                    <p>{profileData.specializations.primary.join(', ')}</p>
                                  </div>
                                </div>
                              )}
                              {profileData.specializations?.sub?.length > 0 && (
                                <div className="col-md-6">
                                  <div className="view-profile-field">
                                    <label>Sub Specializations</label>
                                    <p>{profileData.specializations.sub.join(', ')}</p>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Services Offered */}
                        {(profileData.services_offered?.inspection_type?.length > 0 || 
                          profileData.services_offered?.methodologies?.length > 0 ||
                          profileData.services_offered?.industries_served?.length > 0) && (
                          <div className="view-profile-card mb-4">
                            <h5 className="view-profile-card-title">
                              <FaShoppingBag className="me-2" />
                              Services Offered
                            </h5>
                            <div className="row g-3">
                              {profileData.services_offered?.inspection_type?.length > 0 && (
                                <div className="col-md-6">
                                  <div className="view-profile-field">
                                    <label>Inspection Type</label>
                                    <p>{profileData.services_offered.inspection_type.join(', ')}</p>
                                  </div>
                                </div>
                              )}
                              {profileData.services_offered?.methodologies?.length > 0 && (
                                <div className="col-md-6">
                                  <div className="view-profile-field">
                                    <label>Methodologies</label>
                                    <p>{profileData.services_offered.methodologies.join(', ')}</p>
                                  </div>
                                </div>
                              )}
                              {profileData.services_offered?.industries_served?.length > 0 && (
                                <div className="col-md-6">
                                  <div className="view-profile-field">
                                    <label>Industries Served</label>
                                    <p>{profileData.services_offered.industries_served.join(', ')}</p>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Education */}
                        {profileData.education && profileData.education.length > 0 && (
                          <div className="view-profile-card mb-4">
                            <h5 className="view-profile-card-title">
                              <FaCertificate className="me-2" />
                              Education
                            </h5>
                            {profileData.education.map((edu, index) => (
                              <div key={index} className="mb-3 pb-3 border-bottom">
                                <div className="row g-3">
                                  <div className="col-md-6">
                                    <div className="view-profile-field">
                                      <label>Degree Type</label>
                                      <p>{edu.degree_type || 'N/A'}</p>
                                    </div>
                                  </div>
                                  <div className="col-md-6">
                                    <div className="view-profile-field">
                                      <label>Specialization</label>
                                      <p>{edu.specialization || 'N/A'}</p>
                                    </div>
                                  </div>
                                  <div className="col-md-6">
                                    <div className="view-profile-field">
                                      <label>Institution Name</label>
                                      <p>{edu.institution_name || 'N/A'}</p>
                                    </div>
                                  </div>
                                  <div className="col-md-6">
                                    <div className="view-profile-field">
                                      <label>Year of Completion</label>
                                      <p>{edu.year_of_completion || 'N/A'}</p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Experience */}
                        {profileData.experience && profileData.experience.length > 0 && (
                          <div className="view-profile-card mb-4">
                            <h5 className="view-profile-card-title">
                              <FaUsers className="me-2" />
                              Experience
                            </h5>
                            {profileData.experience.map((exp, index) => (
                              <div key={index} className="mb-3 pb-3 border-bottom">
                                <div className="row g-3">
                                  <div className="col-md-6">
                                    <div className="view-profile-field">
                                      <label>Role Name</label>
                                      <p>{exp.role_name || 'N/A'}</p>
                                    </div>
                                  </div>
                                  <div className="col-md-6">
                                    <div className="view-profile-field">
                                      <label>Level</label>
                                      <p>{exp.level || 'N/A'}</p>
                                    </div>
                                  </div>
                                  <div className="col-md-6">
                                    <div className="view-profile-field">
                                      <label>Years of Experience</label>
                                      <p>{exp.year_of_experience || 'N/A'}</p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Certifications */}
                        {profileData.certifications && profileData.certifications.length > 0 && (
                          <div className="view-profile-card mb-4">
                            <h5 className="view-profile-card-title">
                              <FaCertificate className="me-2" />
                              Certifications
                            </h5>
                            {profileData.certifications.map((cert, index) => (
                              <div key={index} className="mb-3 pb-3 border-bottom">
                                <div className="view-profile-field">
                                  <label>Certificate Details</label>
                                  <p>{cert.details || 'N/A'}</p>
                                </div>
                                {cert.attachment && (
                                  <div className="view-profile-field mt-2">
                                    <a 
                                      href={getFileUrl(cert.attachment)} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="btn btn-sm btn-primary"
                                    >
                                      View Certificate
                                    </a>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Training Programs */}
                        {profileData.training_programs && profileData.training_programs.length > 0 && (
                          <div className="view-profile-card mb-4">
                            <h5 className="view-profile-card-title">
                              <FaCertificate className="me-2" />
                              Training Programs
                            </h5>
                            <div className="view-profile-field">
                              <p>{profileData.training_programs.join(', ')}</p>
                            </div>
                          </div>
                        )}

                        {/* Testimonials */}
                        {profileData.testimonials && profileData.testimonials.length > 0 && (
                          <div className="view-profile-card mb-4">
                            <h5 className="view-profile-card-title">
                              <FaUsers className="me-2" />
                              Reviews and Testimonials
                            </h5>
                            {profileData.testimonials.map((test, index) => (
                              <div key={index} className="mb-3 pb-3 border-bottom">
                                <div className="view-profile-field">
                                  <label>Testimonial Details</label>
                                  <p>{test.details || 'N/A'}</p>
                                </div>
                                {test.attachment && (
                                  <div className="view-profile-field mt-2">
                                    <a 
                                      href={getFileUrl(test.attachment)} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="btn btn-sm btn-primary"
                                    >
                                      View File
                                    </a>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </>
              ) : (
                <>
              {/* Company Information */}
              <div className="view-profile-card mb-4">
                <h5 className="view-profile-card-title">
                  <FaBuilding className="me-2" />
                  Company Information
                </h5>
                <div className="row g-3">
                  <div className="col-md-6">
                    <div className="view-profile-field">
                      <label>Company Name</label>
                      <p>{profile.company_name || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="view-profile-field">
                      <label>Designation</label>
                      <p>{profile.designation || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="view-profile-field">
                      <label>Business Type</label>
                      <p>
                        {businessTypeLabel || (profile.company_type_id ? `ID: ${profile.company_type_id}` : 'N/A')}
                      </p>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="view-profile-field">
                      <label>Registration Number</label>
                      <p>{profile.registration_number || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="view-profile-field">
                      <label>Tax Number (GST)</label>
                      <p>{profile.tax_number || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="view-profile-field">
                      <label>Annual Turnover</label>
                      <p>{profile.annual_turnover || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="view-profile-field">
                      <label>Currency</label>
                      <p>{profile.currency || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="view-profile-field">
                      <label>IEC Number</label>
                      <p>{profile.iec_number || 'N/A'}</p>
                    </div>
                  </div>
                  {profile.export_percentage !== undefined && profile.export_percentage !== null && (
                    <div className="col-md-6">
                      <div className="view-profile-field">
                        <label>Export Percentage</label>
                        <p>{profile.export_percentage}%</p>
                      </div>
                    </div>
                  )}
                  {profile.company_description && (
                    <div className="col-12">
                      <div className="view-profile-field">
                        <label>Company Description</label>
                        <p>{profile.company_description}</p>
                      </div>
                    </div>
                  )}
                  {profile.website && (
                    <div className="col-12">
                      <div className="view-profile-field">
                        <label>Website</label>
                        <p>
                          <a href={profile.website} target="_blank" rel="noopener noreferrer">
                            <FaGlobe className="me-2" />
                            {profile.website}
                          </a>
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Address Information */}
              {profile.address && (
                <div className="view-profile-card mb-4">
                  <h5 className="view-profile-card-title">
                    <FaMapMarkerAlt className="me-2" />
                    Address Information
                  </h5>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <div className="view-profile-field">
                        <label>Address</label>
                        <p>{profile.address.address || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="view-profile-field">
                        <label>Landmark</label>
                        <p>{profile.address.landmark || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="view-profile-field">
                        <label>City</label>
                        <p>{profile.address.city || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="view-profile-field">
                        <label>State</label>
                        <p>{profile.address.state || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="view-profile-field">
                        <label>Country</label>
                        <p>{profile.address.country || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="view-profile-field">
                        <label>Pincode</label>
                        <p>{profile.address.pincode || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="view-profile-field">
                        <label>Timezone</label>
                        <p>{profile.address.timezone || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Overseas Address */}
              {profile.overseas_address && profile.overseas_address.address && (
                <div className="view-profile-card mb-4">
                  <h5 className="view-profile-card-title">
                    <FaMapMarkerAlt className="me-2" />
                    Overseas Address
                  </h5>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <div className="view-profile-field">
                        <label>Address</label>
                        <p>{profile.overseas_address.address || 'N/A'}</p>
                      </div>
                    </div>
                    {profile.overseas_address.landmark && (
                      <div className="col-md-6">
                        <div className="view-profile-field">
                          <label>Landmark</label>
                          <p>{profile.overseas_address.landmark}</p>
                        </div>
                      </div>
                    )}
                    {profile.overseas_address.city && (
                      <div className="col-md-4">
                        <div className="view-profile-field">
                          <label>City</label>
                          <p>{profile.overseas_address.city}</p>
                        </div>
                      </div>
                    )}
                    {profile.overseas_address.state && (
                      <div className="col-md-4">
                        <div className="view-profile-field">
                          <label>State</label>
                          <p>{profile.overseas_address.state}</p>
                        </div>
                      </div>
                    )}
                    {profile.overseas_address.country && (
                      <div className="col-md-4">
                        <div className="view-profile-field">
                          <label>Country</label>
                          <p>{profile.overseas_address.country}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Contact Details */}
              {profile.contact_details && (
                <div className="view-profile-card mb-4">
                  <h5 className="view-profile-card-title">
                    <FaPhone className="me-2" />
                    Contact Details
                  </h5>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <div className="view-profile-field">
                        <label>Alternate Contact Name</label>
                        <p>{profile.contact_details.alternate_contact_name || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="view-profile-field">
                        <label>Alternate Email</label>
                        <p>
                          <FaEnvelope className="me-2" />
                          <a href={`mailto:${profile.contact_details.alternate_email}`}>
                            {profile.contact_details.alternate_email || 'N/A'}
                          </a>
                        </p>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="view-profile-field">
                        <label>Alternate Contact Number</label>
                        <p>
                          <FaPhone className="me-2" />
                          <a href={`tel:${profile.contact_details.alternate_contact_number}`}>
                            {profile.contact_details.alternate_contact_number || 'N/A'}
                          </a>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Main Products */}
              {profile.main_products && profile.main_products.length > 0 && (
                <div className="view-profile-card mb-4">
                  <h5 className="view-profile-card-title">
                    <FaShoppingBag className="me-2" />
                    Main Products
                  </h5>
                  <div className="view-profile-tags">
                    {profile.main_products.map((product, index) => (
                      <span key={index} className="view-profile-tag">
                        {product}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Languages */}
              {profile.languages && profile.languages.length > 0 && (
                <div className="view-profile-card mb-4">
                  <h5 className="view-profile-card-title">
                    <FaLanguage className="me-2" />
                    Languages
                  </h5>
                  <div className="view-profile-tags">
                    {profile.languages.map((language, index) => (
                      <span key={index} className="view-profile-tag">
                        {language}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Certifications */}
              {profile.certifications && profile.certifications.length > 0 && (
                <div className="view-profile-card mb-4">
                  <h5 className="view-profile-card-title">
                    <FaCertificate className="me-2" />
                    Certifications
                  </h5>
                  <div className="view-profile-certifications">
                    {profile.certifications.map((cert, index) => (
                      <div key={index} className="view-profile-certification-item">
                        <h6>{cert.name || 'Unnamed Certificate'}</h6>
                        <p className="mb-1">
                          <strong>Issuer:</strong> {cert.issuer || 'N/A'}
                        </p>
                        {cert.issued_date && (
                          <p className="mb-1">
                            <strong>Issued Date:</strong> {cert.issued_date || 'N/A'}
                          </p>
                        )}
                        <p className="mb-1">
                          <strong>Validity:</strong> {cert.validity || 'N/A'}
                        </p>
                        {cert.certificate_no && (
                          <p className="mb-0">
                            <strong>Certificate No:</strong> {cert.certificate_no}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Quality Control (only for SELLER or BOTH roles) */}
              {qualityDetails && (profile.company_role === 'SELLER' || profile.company_role === 'BOTH') && (
                <div className="view-profile-card mb-4">
                  <h5 className="view-profile-card-title">
                    <FaCheckCircle className="me-2" />
                    Quality Control
                  </h5>
                  <div className="row g-3">
                    {/* Quality Process Description */}
                    {qualityDetails.quality_process_description && (
                      <div className="col-12">
                        <div className="view-profile-field">
                          <label>Quality Control Process</label>
                          <p>{qualityDetails.quality_process_description}</p>
                        </div>
                      </div>
                    )}

                    {/* Quality Certifications */}
                    {qualityDetails.quality_certifications && Array.isArray(qualityDetails.quality_certifications) && qualityDetails.quality_certifications.length > 0 && (
                      <div className="col-12">
                        <div className="view-profile-field">
                          <label>Quality Certifications</label>
                          <div className="view-profile-certifications mt-2">
                            {qualityDetails.quality_certifications.map((cert, index) => (
                              <div key={index} className="view-profile-certification-item">
                                <h6>{cert.name || cert.title || 'Unnamed Certificate'}</h6>
                                {cert.issuer && (
                                  <p className="mb-1">
                                    <strong>Issuer:</strong> {cert.issuer}
                                  </p>
                                )}
                                {cert.date_issued && (
                                  <p className="mb-1">
                                    <strong>Date Issued:</strong> {cert.date_issued ? (cert.date_issued.includes('T') ? new Date(cert.date_issued).toLocaleDateString() : cert.date_issued) : 'N/A'}
                                  </p>
                                )}
                                {cert.expiry_date && (
                                  <p className="mb-1">
                                    <strong>Expiry Date:</strong> {cert.expiry_date ? (cert.expiry_date.includes('T') ? new Date(cert.expiry_date).toLocaleDateString() : cert.expiry_date) : 'N/A'}
                                  </p>
                                )}
                                {cert.certificate_number && (
                                  <p className="mb-1">
                                    <strong>Certificate Number:</strong> {cert.certificate_number}
                                  </p>
                                )}
                                {cert.attachment_path && (
                                  <p className="mb-0">
                                    <strong>Attachment:</strong>{' '}
                                    <a href={cert.attachment_path} target="_blank" rel="noopener noreferrer" className="text-primary">
                                      View Certificate
                                    </a>
                                  </p>
                                )}
                                {cert.status && (
                                  <p className="mb-0 mt-1">
                                    <span className={`badge ${cert.status === 'ACTIVE' ? 'bg-success' : cert.status === 'EXPIRED' ? 'bg-danger' : 'bg-warning'}`}>
                                      {cert.status}
                                    </span>
                                  </p>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Buyer Profile (only for BUYER or BOTH roles) */}
              {profile.buyer_profile && (profile.company_role === 'BUYER' || profile.company_role === 'BOTH') && (
                <div className="view-profile-card mb-4">
                  <h5 className="view-profile-card-title">Buyer Profile Details</h5>
                  <div className="row g-3">
                    {profile.buyer_profile.preferred_suppliers && profile.buyer_profile.preferred_suppliers.length > 0 && (
                      <div className="col-12">
                        <div className="view-profile-field">
                          <label>Preferred Suppliers</label>
                          <div className="view-profile-tags">
                            {profile.buyer_profile.preferred_suppliers.map((supplier, index) => (
                              <span key={index} className="view-profile-tag">{supplier}</span>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                    {profile.buyer_profile.sourcing_regions && profile.buyer_profile.sourcing_regions.length > 0 && (
                      <div className="col-12">
                        <div className="view-profile-field">
                          <label>Sourcing Regions</label>
                          <div className="view-profile-tags">
                            {profile.buyer_profile.sourcing_regions.map((region, index) => (
                              <span key={index} className="view-profile-tag">{region}</span>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                    {profile.buyer_profile.procurement_categories && profile.buyer_profile.procurement_categories.length > 0 && (
                      <div className="col-12">
                        <div className="view-profile-field">
                          <label>Procurement Categories</label>
                          <div className="view-profile-tags">
                            {profile.buyer_profile.procurement_categories.map((category, index) => (
                              <span key={index} className="view-profile-tag">{category}</span>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                    {profile.buyer_profile.sourcing_timeline && (
                      <div className="col-12">
                        <div className="view-profile-field">
                          <label>Sourcing Timeline</label>
                          <p>{profile.buyer_profile.sourcing_timeline}</p>
                        </div>
                      </div>
                    )}
                    
                    {/* Supply Chain Logistics */}
                    {profile.buyer_profile.supply_chain_logistics && (
                      <div className="col-12">
                        <div className="view-profile-field">
                          <label className="mb-3">🚚 Supply Chain Logistics</label>
                          <div className="row g-3">
                            {/* Nearest Port */}
                            {(profile.buyer_profile.supply_chain_logistics.nearest_port || 
                              (profile.buyer_profile.supply_chain_logistics.factory_to_port_distance && 
                               profile.buyer_profile.supply_chain_logistics.factory_to_port_distance.value)) && (
                              <div className="col-12 col-md-6">
                                <div className="view-profile-field">
                                  <label>Nearest Port</label>
                                  <p>
                                    {profile.buyer_profile.supply_chain_logistics.nearest_port || 'N/A'}
                                    {profile.buyer_profile.supply_chain_logistics.factory_to_port_distance && 
                                     profile.buyer_profile.supply_chain_logistics.factory_to_port_distance.value && (
                                      <span className="ms-2 text-muted">
                                        ({profile.buyer_profile.supply_chain_logistics.factory_to_port_distance.value} {profile.buyer_profile.supply_chain_logistics.factory_to_port_distance.unit || 'km'})
                                      </span>
                                    )}
                                  </p>
                                </div>
                              </div>
                            )}
                            
                            {/* Nearest Airport */}
                            {(profile.buyer_profile.supply_chain_logistics.nearest_airport || 
                              (profile.buyer_profile.supply_chain_logistics.factory_to_airport_distance && 
                               profile.buyer_profile.supply_chain_logistics.factory_to_airport_distance.value)) && (
                              <div className="col-12 col-md-6">
                                <div className="view-profile-field">
                                  <label>Nearest Airport</label>
                                  <p>
                                    {profile.buyer_profile.supply_chain_logistics.nearest_airport || 'N/A'}
                                    {profile.buyer_profile.supply_chain_logistics.factory_to_airport_distance && 
                                     profile.buyer_profile.supply_chain_logistics.factory_to_airport_distance.value && (
                                      <span className="ms-2 text-muted">
                                        ({profile.buyer_profile.supply_chain_logistics.factory_to_airport_distance.value} {profile.buyer_profile.supply_chain_logistics.factory_to_airport_distance.unit || 'km'})
                                      </span>
                                    )}
                                  </p>
                                </div>
                              </div>
                            )}
                            
                            {/* Nearest Highway */}
                            {(profile.buyer_profile.supply_chain_logistics.nearest_highway || 
                              (profile.buyer_profile.supply_chain_logistics.factory_to_highway_distance && 
                               profile.buyer_profile.supply_chain_logistics.factory_to_highway_distance.value)) && (
                              <div className="col-12 col-md-6">
                                <div className="view-profile-field">
                                  <label>Nearest Highway</label>
                                  <p>
                                    {profile.buyer_profile.supply_chain_logistics.nearest_highway || 'N/A'}
                                    {profile.buyer_profile.supply_chain_logistics.factory_to_highway_distance && 
                                     profile.buyer_profile.supply_chain_logistics.factory_to_highway_distance.value && (
                                      <span className="ms-2 text-muted">
                                        ({profile.buyer_profile.supply_chain_logistics.factory_to_highway_distance.value} {profile.buyer_profile.supply_chain_logistics.factory_to_highway_distance.unit || 'km'})
                                      </span>
                                    )}
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Seller Profile (only for SELLER or BOTH roles) */}
              {profile.seller_profile && (profile.company_role === 'SELLER' || profile.company_role === 'BOTH') && (
                <div className="view-profile-card mb-4">
                  <h5 className="view-profile-card-title">Seller Profile Details</h5>
                  <div className="row g-3">
                    {/* Export Capabilities */}
                    {profile.seller_profile.export_capabilities && (
                      <div className="col-12">
                        <div className="view-profile-field">
                          <label>Export Capabilities</label>
                          {profile.seller_profile.export_capabilities.countries && profile.seller_profile.export_capabilities.countries.length > 0 && (
                            <div className="mb-2">
                              <strong>Countries:</strong>
                              <div className="view-profile-tags mt-1">
                                {profile.seller_profile.export_capabilities.countries.map((country, index) => (
                                  <span key={index} className="view-profile-tag">{country}</span>
                                ))}
                              </div>
                            </div>
                          )}
                          <div className="row g-2 mt-2">
                            {profile.seller_profile.export_capabilities.experience_years && (
                              <div className="col-md-4">
                                <strong>Experience:</strong> {profile.seller_profile.export_capabilities.experience_years} years
                              </div>
                            )}
                            {profile.seller_profile.export_capabilities.annual_volume && (
                              <div className="col-md-4">
                                <strong>Annual Volume:</strong> {profile.seller_profile.export_capabilities.annual_volume} {profile.seller_profile.export_capabilities.volume_unit || ''}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Manufacturing Types */}
                    {profile.seller_profile.manufacturing_types && profile.seller_profile.manufacturing_types.length > 0 && (
                      <div className="col-12">
                        <div className="view-profile-field">
                          <label>Manufacturing Types</label>
                          <div className="view-profile-tags">
                            {profile.seller_profile.manufacturing_types.map((type, index) => (
                              <span key={index} className="view-profile-tag">{type}</span>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Production Capacity */}
                    {profile.seller_profile.production_capacity && (
                      <div className="col-12">
                        <div className="view-profile-field">
                          <label>Production Capacity</label>
                          <div className="row g-2">
                            {profile.seller_profile.production_capacity.daily && (
                              <div className="col-md-4">
                                <strong>Daily:</strong> {profile.seller_profile.production_capacity.daily} {profile.seller_profile.production_capacity.unit || ''}
                              </div>
                            )}
                            {profile.seller_profile.production_capacity.monthly && (
                              <div className="col-md-4">
                                <strong>Monthly:</strong> {profile.seller_profile.production_capacity.monthly} {profile.seller_profile.production_capacity.unit || ''}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Minimum Order Quantity */}
                    {profile.seller_profile.minimum_order_quantity && (
                      <div className="col-12">
                        <div className="view-profile-field">
                          <label>Minimum Order Quantity</label>
                          <p>
                            {profile.seller_profile.minimum_order_quantity.amount} {profile.seller_profile.minimum_order_quantity.unit || ''}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Main Markets */}
                    {profile.seller_profile.main_markets && profile.seller_profile.main_markets.length > 0 && (
                      <div className="col-12">
                        <div className="view-profile-field">
                          <label>Main Markets</label>
                          <div className="view-profile-tags">
                            {profile.seller_profile.main_markets.map((market, index) => (
                              <span key={index} className="view-profile-tag">{market}</span>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Sample Policy */}
                    {profile.seller_profile.sample_policy && (
                      <div className="col-12">
                        <div className="view-profile-field">
                          <label>Sample Policy</label>
                          <p>{profile.seller_profile.sample_policy}</p>
                        </div>
                      </div>
                    )}

                    {/* R&D Capabilities */}
                    {profile.seller_profile.rd_capabilities && profile.seller_profile.rd_capabilities.length > 0 && (
                      <div className="col-12">
                        <div className="view-profile-field">
                          <label>R&D Capabilities</label>
                          <div className="view-profile-tags">
                            {profile.seller_profile.rd_capabilities.map((capability, index) => (
                              <span key={index} className="view-profile-tag">{capability}</span>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Factory Details (only for SELLER or BOTH roles) */}
              {factoryDetails && factoryDetails.length > 0 && (profile.company_role === 'SELLER' || profile.company_role === 'BOTH') && (
                <div className="view-profile-card mb-4">
                  <h5 className="view-profile-card-title">
                    <FaIndustry className="me-2" />
                    Factory Details
                  </h5>
                  {(() => {
                    // Get factory_details and warehouse_locations from the first factory profile
                    const firstFactory = factoryDetails[0];
                    const factoryDetailsArray = firstFactory.factory_details || [];
                    const warehouseLocationsArray = firstFactory.warehouse_locations || [];
                    
                    return (
                      <div className="row g-3">
                        {/* Factory Details */}
                        {factoryDetailsArray.length > 0 ? (
                          factoryDetailsArray.map((factory, index) => (
                            <div key={index} className="col-12">
                              {factoryDetailsArray.length > 1 && (
                                <h6 className="mb-3">Factory {index + 1}</h6>
                              )}
                              <div className="row g-3">
                                {factory.factory_name && (
                                  <div className="col-md-6">
                                    <div className="view-profile-field">
                                      <label>Factory Name</label>
                                      <p>{factory.factory_name}</p>
                                    </div>
                                  </div>
                                )}
                                {(factory.area_size || factory.area_unit) && (
                                  <div className="col-md-6">
                                    <div className="view-profile-field">
                                      <label>Factory Size</label>
                                      <p>{factory.area_size || 'N/A'} {factory.area_unit || ''}</p>
                                    </div>
                                  </div>
                                )}
                                {factory.workers_count && (
                                  <div className="col-md-6">
                                    <div className="view-profile-field">
                                      <label>Number of Employees</label>
                                      <p>{factory.workers_count}</p>
                                    </div>
                                  </div>
                                )}
                                {factory.total_qc_staff && (
                                  <div className="col-md-6">
                                    <div className="view-profile-field">
                                      <label>Quality Control Staff</label>
                                      <p>{factory.total_qc_staff}</p>
                                    </div>
                                  </div>
                                )}
                                {factory.total_rd_staff && (
                                  <div className="col-md-6">
                                    <div className="view-profile-field">
                                      <label>R&D Staff</label>
                                      <p>{factory.total_rd_staff}</p>
                                    </div>
                                  </div>
                                )}
                                {factory.total_production_lines && (
                                  <div className="col-md-6">
                                    <div className="view-profile-field">
                                      <label>Production Lines</label>
                                      <p>{factory.total_production_lines}</p>
                                    </div>
                                  </div>
                                )}
                                {factory.annual_output && (
                                  <div className="col-md-6">
                                    <div className="view-profile-field">
                                      <label>Annual Output</label>
                                      <p>{factory.annual_output}</p>
                                    </div>
                                  </div>
                                )}
                                {factory.average_lead_time_production && (
                                  <div className="col-md-6">
                                    <div className="view-profile-field">
                                      <label>Average Lead Time (Days)</label>
                                      <p>{factory.average_lead_time_production}</p>
                                    </div>
                                  </div>
                                )}
                                {(factory.address || factory.factory_address) && (
                                  <div className="col-md-12">
                                    <div className="view-profile-field">
                                      <label>Factory Address</label>
                                      <p>
                                        {factory.address || factory.factory_address || ''}
                                        {factory.city && `, ${factory.city}`}
                                        {factory.state && `, ${factory.state}`}
                                        {factory.country && `, ${factory.country}`}
                                        {factory.pincode && ` - ${factory.pincode}`}
                                      </p>
                                    </div>
                                  </div>
                                )}
                              </div>
                              {index < factoryDetailsArray.length - 1 && <hr className="my-4" />}
                            </div>
                          ))
                        ) : (
                          <div className="col-12">
                            <p className="text-muted">No factory details available.</p>
                          </div>
                        )}
                        
                        {/* Warehouse Locations */}
                        {warehouseLocationsArray.length > 0 && (
                          <div className="col-12 mt-4">
                            <h6 className="mb-3">Warehouse Locations</h6>
                            {warehouseLocationsArray.map((warehouse, idx) => (
                              <div key={idx} className="mb-3 p-3 border rounded">
                                {warehouseLocationsArray.length > 1 && (
                                  <h6 className="mb-2">Warehouse {idx + 1}</h6>
                                )}
                                <div className="row g-3">
                                  {warehouse.warehouse_name && (
                                    <div className="col-md-6">
                                      <div className="view-profile-field">
                                        <label>Warehouse Name</label>
                                        <p>{warehouse.warehouse_name}</p>
                                      </div>
                                    </div>
                                  )}
                                  {warehouse.warehouse_stock_quantity && (
                                    <div className="col-md-6">
                                      <div className="view-profile-field">
                                        <label>Stock Quantity</label>
                                        <p>{warehouse.warehouse_stock_quantity}</p>
                                      </div>
                                    </div>
                                  )}
                                  {warehouse.warehouse_address && (
                                    <div className="col-md-12">
                                      <div className="view-profile-field">
                                        <label>Warehouse Address</label>
                                        <p>
                                          {warehouse.warehouse_address}
                                          {warehouse.warehouse_city && `, ${warehouse.warehouse_city}`}
                                          {warehouse.warehouse_state && `, ${warehouse.warehouse_state}`}
                                          {warehouse.warehouse_country && `, ${warehouse.warehouse_country}`}
                                          {warehouse.warehouse_pincode && ` - ${warehouse.warehouse_pincode}`}
                                        </p>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {/* Supply Chain Logistics */}
                        {(firstFactory.nearest_port || firstFactory.distance_port || firstFactory.nearest_airport || firstFactory.distance_airport || firstFactory.highway_name || firstFactory.highway_distance) && (
                          <div className="col-12 mt-4">
                            <h6 className="mb-3">🚚 Supply Chain Logistics</h6>
                            <div className="row g-3">
                              {(firstFactory.nearest_port || firstFactory.distance_port) && (
                                <div className="col-md-6">
                                  <div className="view-profile-field">
                                    <label>Nearest Port</label>
                                    <p>
                                      {firstFactory.nearest_port || 'N/A'}
                                      {firstFactory.distance_port && ` (${firstFactory.distance_port} km)`}
                                    </p>
                                  </div>
                                </div>
                              )}
                              {(firstFactory.nearest_airport || firstFactory.distance_airport) && (
                                <div className="col-md-6">
                                  <div className="view-profile-field">
                                    <label>Nearest Airport</label>
                                    <p>
                                      {firstFactory.nearest_airport || 'N/A'}
                                      {firstFactory.distance_airport && ` (${firstFactory.distance_airport} km)`}
                                    </p>
                                  </div>
                                </div>
                              )}
                              {(firstFactory.highway_name || firstFactory.highway_distance) && (
                                <div className="col-md-6">
                                  <div className="view-profile-field">
                                    <label>Nearest Highway</label>
                                    <p>
                                      {firstFactory.highway_name || 'N/A'}
                                      {firstFactory.highway_distance && ` (${firstFactory.highway_distance} km)`}
                                    </p>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* Shipping Information */}
                        {(firstFactory.shipping_terms || firstFactory.mode_of_transport || firstFactory.dispatch_location || firstFactory.shipping_information) && (
                          <div className="col-12 mt-4">
                            <h6 className="mb-3">🚢 Shipping Information</h6>
                            <div className="row g-3">
                              {firstFactory.shipping_terms && (
                                <div className="col-md-6">
                                  <div className="view-profile-field">
                                    <label>Shipping Terms</label>
                                    <p>{findLabelById(shippingTermsOptions, firstFactory.shipping_terms)}</p>
                                  </div>
                                </div>
                              )}
                              {firstFactory.mode_of_transport && Array.isArray(firstFactory.mode_of_transport) && firstFactory.mode_of_transport.length > 0 && (
                                <div className="col-md-6">
                                  <div className="view-profile-field">
                                    <label>Mode of Transport</label>
                                    <div className="view-profile-tags">
                                      {firstFactory.mode_of_transport.map((mode, idx) => (
                                        <span key={idx} className="view-profile-tag">{findLabelById(modeOfTransportOptions, mode)}</span>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              )}
                              {firstFactory.dispatch_location && (
                                <div className="col-md-6">
                                  <div className="view-profile-field">
                                    <label>Dispatch Location</label>
                                    <p>{findLabelById(countries, firstFactory.dispatch_location)}</p>
                                  </div>
                                </div>
                              )}
                              {firstFactory.shipping_information && (
                                <div className="col-md-12">
                                  <div className="view-profile-field">
                                    <label>Shipping Information</label>
                                    <p>{firstFactory.shipping_information}</p>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* Payment Methods */}
                        {(firstFactory.payment_methods || firstFactory.payment_schedule || firstFactory.late_payment_policy || firstFactory.currency) && (
                          <div className="col-12 mt-4">
                            <h6 className="mb-3">💳 Accepted Payment Methods</h6>
                            <div className="row g-3">
                              {firstFactory.payment_methods && Array.isArray(firstFactory.payment_methods) && firstFactory.payment_methods.length > 0 && (
                                <div className="col-md-6">
                                  <div className="view-profile-field">
                                    <label>Payment Methods</label>
                                    <div className="view-profile-tags">
                                      {firstFactory.payment_methods.map((method, idx) => (
                                        <span key={idx} className="view-profile-tag">{findLabelById(paymentMethodOptions, method)}</span>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              )}
                              {firstFactory.payment_schedule && (
                                <div className="col-md-6">
                                  <div className="view-profile-field">
                                    <label>Payment Schedule</label>
                                    <p>{findLabelById(paymentScheduleOptions, firstFactory.payment_schedule)}</p>
                                  </div>
                                </div>
                              )}
                              {firstFactory.late_payment_policy && (
                                <div className="col-md-6">
                                  <div className="view-profile-field">
                                    <label>Late Payment Policy</label>
                                    <p>{findLabelById(latePaymentPolicyOptions, firstFactory.late_payment_policy)}</p>
                                  </div>
                                </div>
                              )}
                              {firstFactory.currency && (
                                <div className="col-md-6">
                                  <div className="view-profile-field">
                                    <label>Currency</label>
                                    <p>{findLabelById(currencyOptions, firstFactory.currency)}</p>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* Refund Policy */}
                        {(firstFactory.refund_policy !== undefined || firstFactory.refund_policy_description) && (
                          <div className="col-12 mt-4">
                            <h6 className="mb-3">🔄 Refund Policy</h6>
                            <div className="row g-3">
                              <div className="col-md-12">
                                <div className="view-profile-field">
                                  <label>Refund Policy</label>
                                  <p>
                                    {firstFactory.refund_policy ? 'Yes' : 'No'}
                                    {firstFactory.refund_policy_description && (
                                      <span className="ms-2">- {firstFactory.refund_policy_description}</span>
                                    )}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}
                </>
              )}
            </div>

            {/* Right Column - Quick Info */}
            <div className="col-lg-4">
              {/* Quick Stats */}
              <div className="view-profile-card mb-4">
                <h5 className="view-profile-card-title">Quick Information</h5>
                <div className="view-profile-quick-info">
                  <div className="view-profile-quick-item">
                    <FaBuilding className="view-profile-quick-icon" />
                    <div>
                      <label>Company Role</label>
                      <p className="text-capitalize">{profile.company_role || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="view-profile-quick-item">
                    <FaCalendarAlt className="view-profile-quick-icon" />
                    <div>
                      <label>Established</label>
                      <p>{formatYear(profile.establishment_year)}</p>
                    </div>
                  </div>
                  <div className="view-profile-quick-item">
                    <FaUsers className="view-profile-quick-icon" />
                    <div>
                      <label>Employees</label>
                      <p>{profile.number_of_employees || 'N/A'}</p>
                    </div>
                  </div>
                  {profile.profile_completion !== undefined && (
                    <div className="view-profile-quick-item">
                      <FaCheckCircle className="view-profile-quick-icon" />
                      <div>
                        <label>Profile Completion</label>
                        <div className="progress mt-2" style={{ height: '8px' }}>
                          <div 
                            className="progress-bar" 
                            role="progressbar" 
                            style={{ width: `${profile.profile_completion}%` }}
                            aria-valuenow={profile.profile_completion}
                            aria-valuemin="0"
                            aria-valuemax="100"
                          >
                          </div>
                        </div>
                        <p className="mt-1 mb-0">{profile.profile_completion}%</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Social Media */}
              {profile.social_media && (profile.social_media.linkedin || profile.social_media.twitter || profile.social_media.facebook || profile.social_media.instagram) && (
                <div className="view-profile-card mb-4">
                  <h5 className="view-profile-card-title">Social Media</h5>
                  <div className="view-profile-social">
                    {profile.social_media.linkedin && (
                      <a href={profile.social_media.linkedin} target="_blank" rel="noopener noreferrer" className="view-profile-social-link">
                        LinkedIn
                      </a>
                    )}
                    {profile.social_media.twitter && (
                      <a href={profile.social_media.twitter} target="_blank" rel="noopener noreferrer" className="view-profile-social-link">
                        Twitter
                      </a>
                    )}
                    {profile.social_media.facebook && (
                      <a href={profile.social_media.facebook} target="_blank" rel="noopener noreferrer" className="view-profile-social-link">
                        Facebook
                      </a>
                    )}
                    {profile.social_media.instagram && (
                      <a href={profile.social_media.instagram} target="_blank" rel="noopener noreferrer" className="view-profile-social-link">
                        Instagram
                      </a>
                    )}
                  </div>
                </div>
              )}

              {/* Media Files */}
              {profile.media && (
                <div className="view-profile-card mb-4">
                  <h5 className="view-profile-card-title">Media Files</h5>
                  <div className="view-profile-media">
                    {profile.media.company_logo && (
                      <div className="view-profile-media-item">
                        <label>Company Logo</label>
                        <a href={profile.media.company_logo} target="_blank" rel="noopener noreferrer">
                          View Logo
                        </a>
                      </div>
                    )}
                    {profile.media.profile_banner && (
                      <div className="view-profile-media-item">
                        <label>Profile Banner</label>
                        <a href={profile.media.profile_banner} target="_blank" rel="noopener noreferrer">
                          View Banner
                        </a>
                      </div>
                    )}
                    {profile.media.brochure && (
                      <div className="view-profile-media-item">
                        <label>Brochure</label>
                        <a href={profile.media.brochure} target="_blank" rel="noopener noreferrer">
                          Download Brochure
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
            </div>
          )}

          {activeTab === 'rfq' && isBuyer() && (
            <div className="tab-content">
              <BuyerRfq />
            </div>
          )}
        </div>
      </div>
    </>
  );
}

